import type { DeathEvent, PlayerHurtEvent, PlayerPosition, WeaponFireEvent } from "@/lib/demo-parser";

export function drawWeaponFires(
  ctx: CanvasRenderingContext2D,
  weaponFires: WeaponFireEvent[],
  frameTick: number,
  framePlayers: PlayerPosition[],
  toScreenX: (x: number) => number,
  toScreenY: (y: number) => number
) {
  const recent = weaponFires.filter(
    (wf) => wf.tick >= frameTick - 10 && wf.tick <= frameTick
  );

  recent.forEach((wf) => {
    const age = frameTick - wf.tick;
    const opacity = 1 - age / 10;
    const sx = toScreenX(wf.x);
    const sy = toScreenY(wf.y);

    // Outer warm-yellow glow (muzzle flash)
    ctx.fillStyle = `rgba(255, 200, 50, ${opacity * 0.5})`;
    ctx.beginPath();
    ctx.arc(sx, sy, 12, 0, Math.PI * 2);
    ctx.fill();

    // Inner bright white core
    ctx.fillStyle = `rgba(255, 255, 220, ${opacity * 0.9})`;
    ctx.beginPath();
    ctx.arc(sx, sy, 5, 0, Math.PI * 2);
    ctx.fill();

    // Burst lines radiating outward (boltobserv muzzle flash style)
    // Use the firing player's view angle if available, otherwise spread evenly
    const shooter = framePlayers.find((p) => p.steamId === wf.steamId);
    const baseAngle = shooter ? (shooter.viewX * Math.PI) / 180 : 0;
    const burstCount = 6;

    ctx.lineWidth = 1.5;
    for (let i = 0; i < burstCount; i++) {
      const angle = baseAngle + (i / burstCount) * Math.PI * 2;
      const innerR = 8;
      const outerR = 14 + opacity * 8;
      const x1 = sx + Math.cos(angle) * innerR;
      const y1 = sy - Math.sin(angle) * innerR;
      const x2 = sx + Math.cos(angle) * outerR;
      const y2 = sy - Math.sin(angle) * outerR;

      ctx.strokeStyle = `rgba(255, 255, 200, ${opacity * 0.75})`;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }
  });
}

export function drawDeathMarkers(
  ctx: CanvasRenderingContext2D,
  deaths: DeathEvent[],
  roundStartTick: number,
  frameTick: number,
  toScreenX: (x: number) => number,
  toScreenY: (y: number) => number
) {
  const roundDeaths = deaths.filter(
    (d) => d.tick >= roundStartTick && d.tick <= frameTick
  );

  roundDeaths.forEach((d) => {
    const sx = toScreenX(d.x);
    const sy = toScreenY(d.y);

    // Softened death marker — semi-transparent red cross
    ctx.strokeStyle = "rgba(255, 60, 60, 0.55)";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(sx - 9, sy - 9);
    ctx.lineTo(sx + 9, sy + 9);
    ctx.moveTo(sx + 9, sy - 9);
    ctx.lineTo(sx - 9, sy + 9);
    ctx.stroke();
  });
}

export function drawDamageIndicators(
  ctx: CanvasRenderingContext2D,
  hurtEvents: PlayerHurtEvent[],
  framePlayers: PlayerPosition[],
  frameTick: number,
  toScreenX: (x: number) => number,
  toScreenY: (y: number) => number
) {
  const windowTicks = 8;
  const recent = hurtEvents.filter(
    (h) => h.tick >= frameTick - windowTicks && h.tick <= frameTick
  );

  recent.forEach((h) => {
    const player = framePlayers.find((p) => p.steamId === h.victimSteamId && p.isAlive);
    if (!player) return;

    const sx = toScreenX(player.x);
    const sy = toScreenY(player.y);
    const age = frameTick - h.tick;
    const opacity = (1 - age / windowTicks) * 0.85;

    // Expanding ring — yellow for headshots (hitgroup 1), red for body shots
    const dmg = (h as PlayerHurtEvent).dmgHealth ?? 20;
    const isHeadshot = h.hitgroup === 1;
    const ringRadius = 12 + dmg * 0.15 + age * 0.5;
    ctx.strokeStyle = isHeadshot
      ? `rgba(230, 200, 40, ${opacity})`
      : `rgba(190, 44, 44, ${opacity})`;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(sx, sy, ringRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Brighter thin inner ring
    ctx.strokeStyle = isHeadshot
      ? `rgba(255, 230, 80, ${opacity * 0.6})`
      : `rgba(255, 80, 80, ${opacity * 0.6})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(sx, sy, ringRadius - 4, 0, Math.PI * 2);
    ctx.stroke();
  });
}
